import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useAuth } from '../../components/myTrack/AuthContext';
import { rootUrl } from '../../config/config';
import { FaSearch, FaFilter, FaSyncAlt } from 'react-icons/fa';

const LoginAudit = () => {
    const { token } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);

    const [searchTerm, setSearchTerm] = useState('');
    const [successFilter, setSuccessFilter] = useState(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const successOptions = useMemo(() => ([
        { value: 'true', label: 'Success' },
        { value: 'false', label: 'Failure' }
    ]), []);

    const limitOptions = useMemo(() => ([
        { value: 10, label: '10 per page' },
        { value: 20, label: '20 per page' },
        { value: 50, label: '50 per page' }
    ]), []);

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const fetchLoginAudit = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page,
                limit,
                ...(searchTerm && { search: searchTerm }),
                ...(successFilter?.value && { success: successFilter.value }),
                ...(dateFrom && { date_from: dateFrom }),
                ...(dateTo && { date_to: dateTo })
            });

            const response = await axios.get(`${rootUrl}/api/login-audit?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLogs(response.data.data || []);
                setTotalPages(response.data.pagination?.pages || 1);
            } else {
                setError(response.data.message || 'Failed to load login audit logs');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load login audit logs');
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchTerm, successFilter, dateFrom, dateTo, token]);

    useEffect(() => {
        if (!token) return;
        fetchLoginAudit();
    }, [fetchLoginAudit, token]);

    const handleSearch = () => {
        setPage(1);
        fetchLoginAudit();
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setSuccessFilter(null);
        setDateFrom('');
        setDateTo('');
        setPage(1);
        setLimit(20);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Login Audit</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Review login attempts and security activity
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                    <div className="flex items-center mb-4">
                        <FaFilter className="text-[#FF5733] text-lg mr-2" />
                        <h2 className="text-lg font-semibold text-[#2a2e30]">Filters & Search</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Search</label>
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-3 text-gray-400" />
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                    placeholder="Username, IP, reason..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Result</label>
                            <Select
                                classNamePrefix="react-select"
                                className="w-full"
                                options={successOptions}
                                value={successFilter}
                                onChange={setSuccessFilter}
                                isClearable
                                placeholder="All results"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Date From</label>
                            <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Date To</label>
                            <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-4">
                        <div className="w-52">
                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Rows</label>
                            <Select
                                classNamePrefix="react-select"
                                className="w-full"
                                options={limitOptions}
                                value={limitOptions.find(option => option.value === limit) || null}
                                onChange={(option) => setLimit(option ? option.value : 20)}
                                placeholder="Rows per page"
                            />
                        </div>

                        <div className="ml-auto flex gap-2">
                            <button
                                onClick={handleSearch}
                                className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-1 shadow-none"
                            >
                                Search
                            </button>
                            <button
                                onClick={handleResetFilters}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-1"
                            >
                                Reset
                            </button>
                            <button
                                onClick={fetchLoginAudit}
                                className="p-2 rounded-sm bg-slate-100 hover:bg-slate-200 shadow-none flex items-center justify-center"
                                aria-label="Refresh"
                            >
                                <FaSyncAlt className={loading ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                    {error && (
                        <div className="mb-4 text-red-500 text-sm">{error}</div>
                    )}

                    {loading ? (
                        <div className="text-center py-10 text-gray-500">Loading login activity...</div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-10 text-gray-500">No login activity found.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-max table-auto text-left">
                                <thead>
                                    <tr className="bg-gray-50/50">
                                        <th className="p-4 border-b border-blue-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="p-4 border-b border-blue-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                                        <th className="p-4 border-b border-blue-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Username Attempted</th>
                                        <th className="p-4 border-b border-blue-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Result</th>
                                        <th className="p-4 border-b border-blue-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IP</th>
                                        <th className="p-4 border-b border-blue-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User Agent</th>
                                        <th className="p-4 border-b border-blue-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="p-4 border-b border-blue-gray-50 text-sm text-gray-700">
                                                {formatDateTime(log.input_date)}
                                            </td>
                                            <td className="p-4 border-b border-blue-gray-50 text-sm text-gray-700">
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-[#2a2e30]">{log.user_name || '-'}</span>
                                                    <span className="text-xs text-gray-500">{log.user_username || '-'}</span>
                                                </div>
                                            </td>
                                            <td className="p-4 border-b border-blue-gray-50 text-sm text-gray-700">
                                                {log.username_attempted || '-'}
                                            </td>
                                            <td className="p-4 border-b border-blue-gray-50 text-sm">
                                                <span className={`px-2 py-1 rounded-sm text-xs font-semibold ${log.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                                    {log.success ? 'Success' : 'Failure'}
                                                </span>
                                            </td>
                                            <td className="p-4 border-b border-blue-gray-50 text-sm text-gray-700">
                                                {log.ip_address || '-'}
                                            </td>
                                            <td className="p-4 border-b border-blue-gray-50 text-sm text-gray-700 max-w-[260px] truncate" title={log.user_agent || ''}>
                                                {log.user_agent || '-'}
                                            </td>
                                            <td className="p-4 border-b border-blue-gray-50 text-sm text-gray-700 max-w-[240px] truncate" title={log.failure_reason || ''}>
                                                {log.failure_reason || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4">
                            <button
                                onClick={() => setPage(Math.max(1, page - 1))}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-1"
                                disabled={page === 1}
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(Math.min(totalPages, page + 1))}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-1"
                                disabled={page === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginAudit;
